import { useDashboardLocationRanking } from '../../hooks/useDashboard';

interface Props {
  filters: {
    month?: string;
    type?: 'HYGIENE' | 'COMPLIANCE';
    facilityTypeId?: string;
    geoLocationId?: string;
  };
  limit?: number;
}

function scoreColor(score: number): string {
  if (score >= 80) return 'var(--nhip-green)';
  if (score >= 60) return '#F59E0B';
  return 'var(--nhip-red)';
}

function RankBadge({ rank }: { rank: number }) {
  const medal = rank === 1 ? '🥇' : rank === 2 ? '🥈' : rank === 3 ? '🥉' : null;
  if (medal) {
    return <span className='text-base leading-none w-6 text-center'>{medal}</span>;
  }
  return (
    <span
      className='w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0'
      style={{ backgroundColor: 'var(--nhip-surface)', color: 'var(--nhip-ink-muted)' }}
    >
      {rank}
    </span>
  );
}

function DeltaBadge({ delta }: { delta: number }) {
  if (delta === 0) return null;
  const up = delta > 0;
  return (
    <span className='text-[10px] font-semibold' style={{ color: up ? 'var(--nhip-green)' : 'var(--nhip-red)' }}>
      {up ? '▲' : '▼'}{Math.abs(delta)}
    </span>
  );
}

function SkeletonRow() {
  return (
    <div className='flex items-center gap-3 py-2.5 border-b last:border-0' style={{ borderColor: 'var(--nhip-border)' }}>
      <div className='w-6 h-6 rounded-full animate-pulse flex-shrink-0' style={{ backgroundColor: 'var(--nhip-border)' }} />
      <div className='flex-1 animate-pulse space-y-1'>
        <div className='h-3 w-28 rounded' style={{ backgroundColor: 'var(--nhip-border)' }} />
        <div className='h-2 w-20 rounded' style={{ backgroundColor: 'var(--nhip-border)' }} />
      </div>
      <div className='h-3 w-10 rounded animate-pulse' style={{ backgroundColor: 'var(--nhip-border)' }} />
    </div>
  );
}

export default function TopLocations({ filters, limit = 8 }: Props) {
  const { data, isLoading } = useDashboardLocationRanking({
    month: filters.month,
    type: filters.type,
    facilityTypeId: filters.facilityTypeId,
    geoLocationId: filters.geoLocationId,
    limit,
  });

  // API returns `items` array with `geoLocationId`
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const raw = data as any;
  const rows: any[] = raw?.items ?? [];

  return (
    <div
      className='bg-white rounded-xl overflow-hidden flex flex-col'
      style={{ border: '1px solid var(--nhip-border)' }}
    >
      <div className='px-4 py-3 border-b flex items-center justify-between' style={{ borderColor: 'var(--nhip-border)' }}>
        <div>
          <h2 className='text-sm font-semibold' style={{ color: 'var(--nhip-ink)' }}>Top locations</h2>
          <p className='text-[10px] mt-0.5' style={{ color: 'var(--nhip-ink-muted)' }}>
            By average score{raw?.period ? ` · ${raw.period}` : ''}
          </p>
        </div>
        <button
          className='text-xs font-medium flex items-center gap-0.5'
          style={{ color: 'var(--nhip-green)' }}
        >
          Full ranking <span>→</span>
        </button>
      </div>

      <div className='px-4 py-2 flex-1'>
        <div className='flex items-center gap-3 pb-1 border-b mb-1' style={{ borderColor: 'var(--nhip-border)' }}>
          <div className='w-6' />
          <span className='flex-1 text-[10px] font-semibold uppercase tracking-wider' style={{ color: 'var(--nhip-ink-muted)' }}>
            Location
          </span>
          <span className='text-[10px] font-semibold uppercase tracking-wider' style={{ color: 'var(--nhip-ink-muted)' }}>
            Score
          </span>
        </div>

        {isLoading ? (
          Array.from({ length: limit }).map((_, i) => <SkeletonRow key={i} />)
        ) : rows.length === 0 ? (
          <p className='text-xs text-center py-6' style={{ color: 'var(--nhip-ink-muted)' }}>No data for this period</p>
        ) : (
          rows.map((row: any) => (
            <div
              key={row.geoLocationId}
              className='flex items-center gap-3 py-2.5 border-b last:border-0'
              style={{ borderColor: 'var(--nhip-border)' }}
            >
              <RankBadge rank={row.rank} />
              <div className='flex-1 min-w-0'>
                <div className='flex items-center gap-1'>
                  <p className='text-sm font-medium truncate' style={{ color: 'var(--nhip-ink)' }}>
                    {row.locationName}
                  </p>
                  {row.rankDelta != null && <DeltaBadge delta={row.rankDelta} />}
                </div>
                <p className='text-[10px]' style={{ color: 'var(--nhip-ink-muted)' }}>
                  {row.parentName} · {row.assessedFacilities} assessed
                </p>
              </div>
              <span
                className='text-sm font-bold flex-shrink-0'
                style={{ color: scoreColor(row.averageScore) }}
              >
                {Number(row.averageScore).toFixed(1)}%
              </span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
